import { RootState } from '@/app-storage/store.ts'
import LoadingCard from '@/common/components/LoadingCard.tsx'
import { ChildrenProp, RequestError } from '@/common/types_and_interfaces.ts'
import { Navigate } from 'react-router-dom'

import { FullCenteredMain as AuthSessionLayout } from '@/layouts/FullCenteredLayout.tsx'
import { CommonQueries } from '@/queries/common/queries'
import { UserQueries } from '@/queries/users/queries.ts'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { usePrefetchQuery, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { US_STATES_ID } from '../common/constants'

import { Button } from '@/common/components/Button.tsx'
import { useTranslation } from 'react-i18next'
import { useSignOut } from '../common/hooks/useSignOut'

export default function AuthSessionMonitor({ children }: ChildrenProp) {
  const { t: translate } = useTranslation()
  const AuthSessionState = useSelector((state: RootState) => state.AuthSession)
  usePrefetchQuery(CommonQueries.FetchStateByContry(US_STATES_ID))
  const application = useSelector((state: RootState) => state.FamilyApplication)
  const SessionUserQuery = useQuery(
    UserQueries.getAuthenticatedUser(application.data?.applicant_id ?? -1)
  )
  const signOut = useSignOut()

  useEffect(() => {
    const epochNow = Date.now()
    let timerID = 0
    // Handle base case
    if (AuthSessionState?.remember_me == false) {
      if (epochNow > AuthSessionState.auth_badge.expires_at) {
        signOut()
      } else {
        timerID = window.setTimeout(
          () => {},
          AuthSessionState.auth_badge.expires_at - epochNow
        )
      }
    }

    return () => clearTimeout(timerID)
  }, [])

  if (SessionUserQuery.status === 'pending') {
    return (
      <AuthSessionLayout>
        <LoadingCard isLoading={true}>
          <span>{translate('authorizing')}</span>
        </LoadingCard>
      </AuthSessionLayout>
    )
  }

  

  if (SessionUserQuery.status === 'error') {
    // Parse the error to extract code and message
    // Note: React Query's error type is `Error | unknown`, but GETMe() throws a RequestError object
    // (from parseRequestError). We need to assert the type to access RequestError properties (code, message).
    // TODO: Consider updating the query's error type generic or using parseRequestError here for type safety
    // @ts-expect-error - SessionUserQuery.error is typed as Error, but the actual thrown value is a RequestError object
    const parsedError = SessionUserQuery.error as RequestError
    const errorCode = parsedError.code
    const errorMessage = parsedError.message

    // Display custom title for error code 218
    const APPLICATION_STILL_IN_PROGRESS_ERROR_CODE = 218
    const errorTitle =
      errorCode === APPLICATION_STILL_IN_PROGRESS_ERROR_CODE
        ? 'Application Still in Progress'
        : translate('signed_out_title')

    return (
      <AuthSessionLayout>
        <LoadingCard>
          <h1 className="mb-4 text-gray-800 text-2xl font-medium">
            {errorTitle}
          </h1>
          <div className="space-y-4 text-center">
            <p className="font-normal text-base text-gray-600">
              {translate('signed_out_message')}
            </p>
            <p className="font-normal text-base max-w-xl  text-gray-600">
              {errorMessage}
            </p>
          </div>
          <Button
            className="mt-10"
            size="lg"
            onClick={() => {
              signOut()
            }}
          >
            {translate('back_to_login')}
          </Button>
        </LoadingCard>
      </AuthSessionLayout>
    )
  }

  if (!AuthSessionState.isAuthenticated) {
    return <Navigate to={AppRoutePaths.auth.login} />
  }
  return <>{children}</>
}
